import { useCallback, useEffect, useState } from 'react'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import CircularProgress from '@mui/joy/CircularProgress'
import Divider from '@mui/joy/Divider'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Snackbar from '@mui/joy/Snackbar'
import Switch from '@mui/joy/Switch'
import Typography from '@mui/joy/Typography'
import { growthCampaignApi, GrowthCampaignConfig } from './api'

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

const EMPTY: GrowthCampaignConfig = { enabled: false, flashOfferEnabled: false, flashOfferStripeUrl: '' }

export default function GrowthCampaignPage() {
    const [form, setForm] = useState<GrowthCampaignConfig>(EMPTY)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    const notify = useCallback((message: string, color: 'success' | 'danger') => setSnackbar({ open: true, message, color }), [])

    useEffect(() => {
        let cancelled = false
        growthCampaignApi
            .getConfig()
            .then((cfg) => {
                if (!cancelled) setForm(cfg)
            })
            .catch(() => notify('Failed to load growth campaign config', 'danger'))
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [notify])

    const handleSave = useCallback(async () => {
        setSaving(true)
        try {
            const saved = await growthCampaignApi.updateConfig({
                enabled: form.enabled,
                flashOfferEnabled: form.flashOfferEnabled,
                flashOfferStripeUrl: form.flashOfferStripeUrl.trim(),
            })
            setForm(saved)
            notify('Growth campaign config saved', 'success')
        } catch {
            notify('Failed to save growth campaign config', 'danger')
        } finally {
            setSaving(false)
        }
    }, [form, notify])

    // Guard the operator from turning the flash phase on with no checkout link — the server hides
    // the flash CTA in that case anyway, but flag it here so it isn't a silent no-op.
    const flashOnWithoutUrl = form.flashOfferEnabled && form.flashOfferStripeUrl.trim().length === 0

    return (
        <Box sx={{ p: 3, maxWidth: 760, mx: 'auto' }}>
            <Box sx={{ mb: 0.5 }}>
                <Typography level="h3" sx={{ fontWeight: 700 }}>
                    Growth Campaign
                </Typography>
                <Typography level="body-sm" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                    Master toggle for the sequential growth offers shown to Free / trial users on a DAT visit: 3-day unlimited emails, then an 8-hour 90%-off
                    flash offer. Changes propagate within ~30s.
                </Typography>
            </Box>

            <Divider sx={{ my: 2.5 }} />

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Card variant="outlined" sx={{ gap: 2.5, p: 3 }}>
                    <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <FormLabel>Campaign enabled</FormLabel>
                            <FormHelperText>Master switch. Off = no windows or modals for anyone.</FormHelperText>
                        </Box>
                        <Switch checked={form.enabled} onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))} />
                    </FormControl>

                    <Divider />

                    <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                            <FormLabel>Flash offer enabled</FormLabel>
                            <FormHelperText>Enables the 8-hour 90%-off phase after the unlimited window ends.</FormHelperText>
                        </Box>
                        <Switch
                            checked={form.flashOfferEnabled}
                            onChange={(e) => setForm((f) => ({ ...f, flashOfferEnabled: e.target.checked }))}
                            disabled={!form.enabled}
                        />
                    </FormControl>

                    <Divider />

                    <FormControl error={flashOnWithoutUrl}>
                        <FormLabel>Flash offer Stripe URL</FormLabel>
                        <Input
                            value={form.flashOfferStripeUrl}
                            onChange={(e) => setForm((f) => ({ ...f, flashOfferStripeUrl: e.target.value }))}
                            placeholder="https://buy.stripe.com/…"
                        />
                        <FormHelperText>
                            {flashOnWithoutUrl
                                ? 'Flash offer is enabled but no URL is set — the flash CTA will stay hidden until you add the link.'
                                : 'Live Stripe checkout link with the 90%/first-month coupon. Overrides the FLASH_OFFER_STRIPE_URL env var.'}
                        </FormHelperText>
                    </FormControl>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button onClick={handleSave} loading={saving}>
                            Save
                        </Button>
                    </Box>
                </Card>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                color={snackbar.color}
                variant="soft"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}
